import { IEnvironment, ITenantConfigResponse, TenantType } from '@inshuwa-client/models';
import { deriveSecondaryColor, generateColorPalette } from '../utils/color-palette';

type EnvironmentBrandingFields = Pick<
  IEnvironment,
  | 'secondaryColor'
  | 'logoUrl'
  | 'secondaryLogoUrl'
  | 'favIcon'
  | 'insurerId'
  | 'partnerId'
  | 'intermediaryId'
  | 'colorPlate'
>;

type EnvironmentTenantIds = Pick<IEnvironment, 'insurerId' | 'partnerId' | 'intermediaryId'>;

/**
 * What to merge onto `environment` for a fetched config. `name` is deliberately left untouched
 * by the caller - it's a closed, build-time enum some route guards branch on, unlike
 * `portalName`, which is a plain display string.
 */
export function computeEnvironmentPatch(
  config: ITenantConfigResponse,
  current: EnvironmentBrandingFields,
): Partial<IEnvironment> {
  return {
    primaryColor: config.primaryColor,
    ...resolveChartColors(config, current),
    // Same `||` fallback the two lines below already use, and for the same reason: a tenant whose
    // branding row has no logo (and the default config, which sends "") would otherwise blank the
    // build's own logo, and `[ngSrc]=""` is a hard NG02952 - it throws rather than degrading to a
    // broken image, on ~26 templates including every login page.
    logoUrl: config.logoUrl || current.logoUrl,
    secondaryLogoUrl: config.secondaryLogoUrl ?? current.secondaryLogoUrl,
    favIcon: config.faviconUrl || current.favIcon,
    portalName: config.portalName,
    // {} for a default/unconfigured tenant - every flag reads falsy, matching today's fallback
    // behavior for any tenant not on a route guard's allowlist.
    featureFlags: config.isDefault ? {} : (config.featureFlags ?? {}),
    ...(config.isDefault ? currentTenantIds(current) : resolveTenantIds(config.tenantType, config.tenantId, current)),
  };
}

/**
 * A branded tenant must not inherit the build's leftover secondary colour or chart palette: those
 * are Inshuwa's own colours, and the dashboards feed them straight into chart ramps and legend
 * swatches, so a Swan page ends up drawing Inshuwa magenta beside Swan blue. Both are derived from
 * the tenant's primary instead. A default (unbranded) domain genuinely IS Inshuwa, so it keeps
 * whatever the build shipped with.
 */
function resolveChartColors(
  config: ITenantConfigResponse,
  current: EnvironmentBrandingFields,
): Pick<IEnvironment, 'secondaryColor' | 'colorPlate'> {
  if (config.isDefault) {
    return { secondaryColor: current.secondaryColor, colorPlate: current.colorPlate };
  }

  const secondaryColor = config.secondaryColor ?? deriveSecondaryColor(config.primaryColor);
  return {
    secondaryColor,
    // An explicitly configured palette always wins; the derived ramp is only the fallback for
    // tenants that haven't chosen chart colours.
    colorPlate: config.colorPalette?.length
      ? config.colorPalette
      : generateColorPalette(config.primaryColor, secondaryColor, 5),
  };
}

// Insurer/Broker/Partner map to insurerId/intermediaryId/partnerId respectively - intermediaryId
// is the existing generic field for both Broker and Agent (matching the backend's Intermediary
// base class). Only the resolved type's id changes; the other two keep their current value.
function resolveTenantIds(
  tenantType: TenantType,
  tenantId: number,
  current: EnvironmentTenantIds,
): EnvironmentTenantIds {
  switch (tenantType) {
    case 'Insurer':
      return { ...currentTenantIds(current), insurerId: tenantId };
    case 'Broker':
      return { ...currentTenantIds(current), intermediaryId: tenantId };
    case 'Partner':
      return { ...currentTenantIds(current), partnerId: tenantId };
  }
}

function currentTenantIds(current: EnvironmentTenantIds): EnvironmentTenantIds {
  return {
    insurerId: current.insurerId,
    partnerId: current.partnerId,
    intermediaryId: current.intermediaryId,
  };
}
